import { Link } from 'react-router-dom';
import { LegalLayout, Lead, Section, P, Bullets } from '../legal/LegalLayout';

export default function Privacy() {
  return (
    <LegalLayout title="Privacy" updated="29 August 2026">
      <Lead>
        Mātou Collective is an Indigenous-led collective. We build and run
        digital infrastructure so that communities can hold their own data.
        This policy explains what we collect, why, and the control you keep
        over it. We have written it plainly on purpose. If anything here is
        unclear, please ask.
      </Lead>

      <Section title="Where we start">
        <P>
          Your data belongs to you and your community. It does not belong to
          us, and it is never something we would sell. We hold it as kaitiaki
          (guardians), on infrastructure the collective runs itself. This is
          what data sovereignty means to us: the people a set of data is about
          keep the authority over it.
        </P>
      </Section>

      <Section title="What we collect">
        <Bullets
          items={[
            'Account details you give us — your name, your email address, and a password you set. Passwords are stored encrypted, never in plain form.',
            'The content you choose to store — the files, messages, and documents you keep in the services you use.',
            'Basic technical records — sign-in times, device and network information, and error logs. We keep these only to run the services and keep them secure.',
          ]}
        />
      </Section>

      <Section title="Why we collect it">
        <P>
          Only to give you your account, run the services you have asked for,
          and keep them safe and working. Nothing else. We do not build
          profiles of you, and we do not analyse your data for our own gain.
        </P>
      </Section>

      <Section title="Where your data lives">
        <P>
          On community-run servers operated by the collective, not on large
          commercial cloud platforms. We choose where data is hosted with care
          and keep it close to the communities it serves.
        </P>
      </Section>

      <Section title="Who can see it">
        <Bullets
          items={[
            'You, and the people you deliberately share it with.',
            'A small number of operators who keep the system running. They can reach your data only when it is needed to fix or protect a service, and they are bound to respect it.',
          ]}
        />
      </Section>

      <Section title="What we never do">
        <Bullets
          items={[
            'We never sell your data.',
            'We never show you advertising or track you for profit.',
            'We never hand your data to other organisations for their own use.',
            'If the law ever compels us to disclose something, we will give only what is required, resist over-reach, and tell you wherever we are allowed to.',
          ]}
        />
      </Section>

      <Section title="Your control">
        <P>
          This is your data to govern — tino rangatiratanga, self-determination,
          over what is yours. You can see the data we hold about you, correct
          it, export it, and delete it. You can leave at any time and take your
          data with you. Ask us and we will help.
        </P>
      </Section>

      <Section title="The Matou app">
        <P>
          The Matou app (Android and desktop) is how members take part in
          their community — registration, profiles, chat, notices, projects
          and decisions. It is built so the community, not us, holds the data.
          A few things are worth saying specifically:
        </P>
        <Bullets
          items={[
            'Your identity lives on your device. The app creates your cryptographic identity and a recovery phrase there. The recovery phrase is stored in your device’s secure storage and is never sent to us or to anyone — we cannot see it and we will never ask for it.',
            'When you register, the details you enter — your name, email, and what you tell the community about yourself — go to your community’s administrators so they can welcome you in. The profile you choose to share is visible to other members of your community, and to no one else.',
            'Messages, notices and project records are synchronised directly between the devices of the community’s members, encrypted in transit and governed by cryptographic access rules. There is no central store of everyone’s conversations.',
            'The app contains no advertising, no analytics, and no trackers. It asks for no device permissions beyond network access, and a photo only if you choose one for your profile.',
            'The app is for adults, eighteen and over. Communities are administrator-moderated; see our child safety standards below.',
          ]}
        />
        <P>
          Read our{' '}
          <Link
            to="/child-safety"
            className="text-matou-teal-deep underline hover:text-matou-teal-medium"
          >
            Child safety standards
          </Link>{' '}
          for how content concerns in the app are handled.
        </P>
      </Section>

      <Section title="Cookies">
        <P>
          We use only the cookies needed to sign you in and keep you signed in.
          No advertising cookies, and no tracking cookies.
        </P>
      </Section>

      <Section title="Changes">
        <P>
          If we change this policy, we will update the date above and, for
          anything significant, tell you directly.
        </P>
      </Section>

      <Section title="Contact">
        <P>
          Questions about your data or this policy are welcome at{' '}
          <a
            href="mailto:info@matou.nz"
            className="text-matou-teal-deep underline hover:text-matou-teal-medium"
          >
            info@matou.nz
          </a>
          . You can also read our{' '}
          <Link
            to="/terms"
            className="text-matou-teal-deep underline hover:text-matou-teal-medium"
          >
            Terms of use
          </Link>
          .
        </P>
      </Section>
    </LegalLayout>
  );
}
